import React from 'react';
import { useConfig } from 'ha/helpers/config';
import { useSelector } from 'react-redux';

import {
  getUserId,
  getUserIdAlias,
  getUserTraits,
  getAnalyticsUserData,
} from 'ha/modules/AuthLogic/selectors';
import { getUserIdentifier } from 'ha/modules/Experiments/selectors';

import {
  intercomAPI,
  transformUserData,
  isIntercomInitialised,
  loadIntercomScript,
  createIntercomPlaceholder,
} from '../utils';
import { UserData } from '../utils/transformUserData';

interface Props {
  isLauncherVisible: boolean;
}

const LocalIntercom: React.FC<Props> = ({ isLauncherVisible }) => {
  const userId = useSelector(getUserId);
  const aliasId = useSelector(getUserIdAlias);
  const userTraits = useSelector(getUserTraits);
  const userDataAnalytics = useSelector(getAnalyticsUserData);
  const userIdentifier = useSelector(getUserIdentifier);

  const {
    intercom: { appID },
  } = useConfig();

  const updateUserInfo = React.useCallback(() => {
    intercomAPI.update(
      transformUserData({
        userId: userId ? aliasId : userIdentifier,
        ...userTraits,
        ...userDataAnalytics,
      } as UserData),
    );
  }, [aliasId, userDataAnalytics, userId, userIdentifier, userTraits]);

  const handleScriptLoaded = React.useCallback(() => {
    if (isLauncherVisible) {
      intercomAPI.showLauncher();
    }

    updateUserInfo();
  }, [isLauncherVisible, updateUserInfo]);

  const initIntercom = React.useCallback(() => {
    if (!window.intercomSettings && appID) {
      window.intercomSettings = {
        app_id: appID,
      };
    }

    createIntercomPlaceholder();

    loadIntercomScript(appID, handleScriptLoaded);
  }, [appID, handleScriptLoaded]);

  React.useEffect(() => {
    if (isIntercomInitialised()) {
      return;
    }

    initIntercom();
  }, [initIntercom]);

  return null;
};

export { LocalIntercom };
